/**
 * Le barème du Montage — SPEC-UI §5.3, recopié à la lettre.
 *
 * ```
 * Appariement : chaque hit est apparié au cut requis non servi le plus proche
 *               si |Δ| ≤ W/2, sinon le hit est ORPHELIN.
 * Par cut :  Δ ≤ W/4          → 100
 *            W/4 < Δ ≤ W/2    → 100 − 60 × (Δ − W/4) / (W/4)      (→ 40 au bord)
 *            non servi        → 0
 * Pénalité : −4 pts par hit orphelin, plafonnée à −20
 * Score = clamp(0, 100, moyenne des cuts − pénalité)
 * ```
 *
 * ⚠️ **Ce barème n'est pas autoritatif.** Le scoring qui compte est **serveur**
 * (`server/src/atelier/validate.ts`, tâche M2.9) : le client soumettra sa timeline de hits et
 * recevra le score. En bac à sable, il n'y a que ce calcul-ci — d'où l'intérêt de le garder
 * **pur et isolé** : le jour où le serveur arrive, on jette l'appel, pas le jeu.
 *
 * Fonction pure, aucune dépendance : c'est aussi ce qui la rend testable telle quelle.
 */

/** Pistes du multicam (SPEC-UI §5.3) : F, J, puis Espace à E5. */
export type Piste = "A" | "B" | "C";

export interface Hit {
  atMs: number;
  /** Piste frappée. Absente en mono-piste : tout est bon. */
  track?: Piste;
}

export interface Appariement {
  /** Index du cut servi, ou `null` si le hit est orphelin. */
  cut: number | null;
  /** Écart signé hit − cut, en ms (`null` pour un orphelin). */
  delta: number | null;
  /** Instant du hit, en ms depuis le début de la piste. */
  hitMs: number;
  /** Multicam : le cut a été servi, mais avec la mauvaise touche — il vaut moitié moins. */
  mauvaisePiste: boolean;
}

/** Multicam : « mauvaise touche dans la fenêtre → valeur du cut × 0,5 » (§5.3). */
export const MALUS_MAUVAISE_PISTE = 0.5;

export const PENALITE_ORPHELIN = 4;
export const PENALITE_MAX = 20;

/**
 * Appariement glouton, **dans l'ordre des hits** — c'est la règle littérale de §5.3.
 *
 * ⟦A-59⟧ `fenetres` porte la fenêtre propre à chaque cut quand le repérage en a élargi
 * certains. À `undefined`, chaque cut est comparé à `windowMs` : le jeu d'avant, à l'identique.
 */
export function apparier(
  cuts: readonly { atMs: number; track?: Piste }[],
  hits: readonly (Hit | number)[],
  windowMs: number,
  fenetres?: readonly number[] | null,
): Appariement[] {
  const servis = new Set<number>();
  return hits.map((brut) => {
    const hit: Hit = typeof brut === "number" ? { atMs: brut } : brut;
    let meilleur = -1;
    let meilleurEcart = Infinity;
    for (let i = 0; i < cuts.length; i++) {
      if (servis.has(i)) continue;
      const ecart = Math.abs(hit.atMs - cuts[i]!.atMs);
      if (ecart <= (fenetres?.[i] ?? windowMs) / 2 && ecart < meilleurEcart) {
        meilleur = i;
        meilleurEcart = ecart;
      }
    }
    if (meilleur < 0) return { cut: null, delta: null, hitMs: hit.atMs, mauvaisePiste: false };
    servis.add(meilleur);
    const attendue = cuts[meilleur]!.track;
    return {
      cut: meilleur,
      delta: hit.atMs - cuts[meilleur]!.atMs,
      hitMs: hit.atMs,
      // En mono-piste, `track` est absent des deux côtés : rien à comparer.
      mauvaisePiste: attendue !== undefined && hit.track !== undefined && hit.track !== attendue,
    };
  });
}

/** Valeur d'un cut servi avec un écart `delta` (ms), selon le barème §5.3. */
export function valeurDuCut(delta: number, windowMs: number): number {
  const quart = windowMs / 4;
  const ecart = Math.abs(delta);
  if (ecart <= quart) return 100;
  if (ecart <= quart * 2) return 100 - 60 * ((ecart - quart) / quart);
  return 0;
}

export interface ResultatMontage {
  score: number;
  /** Cuts servis dans le quart central — le « parfait » qu'on affiche à la volée. */
  parfaits: number;
  /** Cuts servis, mais en bord de fenêtre. */
  justes: number;
  rates: number;
  orphelins: number;
  penalite: number;
  /** ⟦A-59⟧ La part « piste » du score, et la note du repérage — `null` sans la phase. */
  piste: number;
  reperage: NoteReperage | null;
}

export function noter(
  cuts: readonly { atMs: number; track?: Piste }[],
  appariements: readonly Appariement[],
  windowMs: number,
  scoreCap = 100,
  /** ⟦A-59⟧ Les fenêtres par cut et le repérage — absents, c'est le barème d'avant. */
  reperage?: { fenetres: readonly number[]; plan: OptionsReperage } | null,
): ResultatMontage {
  const valeurs = new Array<number>(cuts.length).fill(0);
  let orphelins = 0;
  let parfaits = 0;
  let justes = 0;

  for (const a of appariements) {
    if (a.cut === null || a.delta === null) {
      orphelins++;
      continue;
    }
    const fenetre = reperage?.fenetres[a.cut] ?? windowMs;
    const valeur = valeurDuCut(a.delta, fenetre) * (a.mauvaisePiste ? MALUS_MAUVAISE_PISTE : 1);
    valeurs[a.cut] = valeur;
    if (Math.abs(a.delta) <= fenetre / 4) parfaits++;
    else justes++;
  }

  const penalite = Math.min(PENALITE_MAX, orphelins * PENALITE_ORPHELIN);
  const moyenne =
    cuts.length === 0 ? 0 : valeurs.reduce((total, v) => total + v, 0) / cuts.length;
  const piste = Math.max(0, Math.min(100, moyenne - penalite));

  const plan = reperage ? noterReperage(reperage.plan, cuts, valeurs) : null;
  // ⟦A-59⟧ Sans repérage, cette ligne rend `piste` — pas « à peu près », le même nombre.
  const brut = plan === null ? piste : (1 - REPERAGE_POIDS) * piste + REPERAGE_POIDS * plan.score;

  return {
    score: Math.max(0, Math.min(scoreCap, Math.round(brut))),
    parfaits,
    justes,
    rates: cuts.length - parfaits - justes,
    orphelins,
    penalite,
    piste: Math.round(piste),
    reperage: plan,
  };
}

/** Raccourci : de la timeline brute au score. */
export function scorer(
  cuts: readonly { atMs: number; track?: Piste }[],
  hits: readonly (Hit | number)[],
  windowMs: number,
  scoreCap = 100,
  reperage?: { fenetres: readonly number[]; plan: OptionsReperage } | null,
): ResultatMontage {
  return noter(
    cuts,
    apparier(cuts, hits, windowMs, reperage?.fenetres),
    windowMs,
    scoreCap,
    reperage,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-59⟧ « Le repérage » — jumeau **non autoritatif** de `server/src/atelier/scoring.ts`
// ─────────────────────────────────────────────────────────────────────────────
//
// Comme partout ici : le serveur note, ce module ne fait que montrer au joueur ce que le
// serveur trouvera. La moindre divergence produirait l'écart le plus corrosif qui soit —
// invisible, systématique, et impossible à diagnostiquer en jouant.

/** ⟦A-50⟧ L'allègement : plancher acquis sur tous les cuts, mérite gagné cut par cut. */
export const REPERAGE_FENETRE_PLANCHER = 0.06;
export const REPERAGE_FENETRE_MERITE = 0.34;
/** Poids du repérage dans le score de l'étape. */
export const REPERAGE_POIDS = 0.25;

/** Ce dont le barème du repérage a besoin : le rayon, et les marqueurs posés (en ms de piste). */
export interface OptionsReperage {
  rayonMs: number;
  marqueurs: readonly number[];
}

export interface Annonce {
  cut: number | null;
  proximite: number;
  note: number;
}

export interface NoteReperage {
  score: number;
  annonces: Annonce[];
}

/**
 * Où chaque marqueur a **fini**, en ms de piste — reconstruit exactement comme le serveur le
 * reconstruira : la dernière commande soumise avant `finishedAtMs`, à défaut le départ.
 *
 * On ne lit jamais la position affichée. C'est ce qui garantit que le chiffre de l'écran est
 * celui que le serveur écrira.
 */
export function marqueursPoses(
  plan: { chronoMs: number; departs: readonly number[] },
  durationMs: number,
  soumis: { poses: readonly { marqueur: number; atMs: number; position: number }[]; finishedAtMs: number } | undefined,
): number[] {
  const fin = Math.max(0, Math.min(plan.chronoMs, soumis?.finishedAtMs ?? 0));
  const poses = [...(soumis?.poses ?? [])].sort((a, b) => a.atMs - b.atMs);
  return plan.departs.map((depart, i) => {
    let position = depart;
    for (const p of poses) {
      if (p.marqueur !== i || p.atMs > fin) continue;
      position = p.position;
    }
    return position * durationMs;
  });
}

/**
 * La fenêtre de chaque cut après repérage. Le mérite **ne se cumule pas** entre marqueurs : on
 * prend le meilleur — le confort rayonne, il ne s'empile pas.
 */
export function fenetresApresReperage(
  cuts: readonly { atMs: number }[],
  windowMs: number,
  plan: OptionsReperage,
): number[] {
  const rayon = Math.max(1, plan.rayonMs);
  return cuts.map((cut) => {
    let gain = 0;
    for (const m of plan.marqueurs) gain = Math.max(gain, 1 - Math.abs(cut.atMs - m) / rayon);
    return (
      windowMs *
      (1 + REPERAGE_FENETRE_PLANCHER + REPERAGE_FENETRE_MERITE * Math.max(0, Math.min(1, gain)))
    );
  });
}

/**
 * Le barème de la phase — **copie conforme** de `noterReperage` côté serveur.
 *
 * Un marqueur annonce le cut le plus proche, et sa note vaut `proximité × ce que ce cut a
 * valu` : l'élargissement est acquis d'avance, la note ne l'est pas. Un cut ne peut être
 * annoncé qu'une fois — sinon empiler ses marqueurs sur le cut le plus facile serait dominant.
 */
export function noterReperage(
  plan: OptionsReperage,
  cuts: readonly { atMs: number }[],
  valeursCuts: readonly number[],
): NoteReperage {
  const rayon = Math.max(1, plan.rayonMs);
  const paires: { m: number; c: number; d: number }[] = [];
  plan.marqueurs.forEach((position, m) => {
    cuts.forEach((cut, c) => {
      const d = Math.abs(cut.atMs - position);
      if (d < rayon) paires.push({ m, c, d });
    });
  });
  paires.sort((a, b) => a.d - b.d || a.m - b.m || a.c - b.c);

  const annonces: Annonce[] = plan.marqueurs.map(() => ({ cut: null, proximite: 0, note: 0 }));
  const cutsPris = new Set<number>();
  for (const { m, c, d } of paires) {
    if (annonces[m]!.cut !== null || cutsPris.has(c)) continue;
    cutsPris.add(c);
    const proximite = Math.max(0, Math.min(1, 1 - d / rayon));
    annonces[m] = { cut: c, proximite, note: proximite * (valeursCuts[c] ?? 0) };
  }

  const score = annonces.reduce((s, a) => s + a.note, 0) / Math.max(1, annonces.length);
  return { score: Math.max(0, Math.min(100, score)), annonces };
}
